import React, { useState, useEffect, useRef } from 'react';
import { useLanguage } from '../i18n/LanguageContext';
import useVerseSearch from '../utils/useVerseSearch';
import './VerseSearch.css';

export function VerseSearchFAB({ onClick }) {
  const { t } = useLanguage();
  return (
    <button className="verse-search-fab tooltip-wrap" onClick={onClick} aria-label={t('verseSearch.placeholder')}>
      <span className="verse-search-fab-icon">📖</span>
      <span className="tooltip tooltip-left">{t('verseSearch.placeholder')}</span>
    </button>
  );
}

export default function VerseSearchModal({ isOpen, onClose }) {
  const { t } = useLanguage();
  const inputRef = useRef(null);
  const { query, results, loading, error, search, clear } = useVerseSearch();
  const [expandedIdx, setExpandedIdx] = useState(null);

  useEffect(() => {
    if (isOpen) setTimeout(() => inputRef.current?.focus(), 100);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  const handleClose = () => {
    clear();
    setExpandedIdx(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="verse-search-overlay" onClick={handleClose}>
      <div className="verse-search-modal" onClick={(e) => e.stopPropagation()}>
        <div className="verse-search-header">
          <div className="verse-search-input-wrap">
            <span className="verse-search-input-icon">🔍</span>
            <input
              ref={inputRef}
              className="verse-search-input"
              type="text"
              value={query}
              onChange={(e) => { search(e.target.value); setExpandedIdx(null); }}
              placeholder={t('verseSearch.placeholder')}
            />
            {query && (
              <button className="verse-search-clear" onClick={() => { clear(); setExpandedIdx(null); }}>✕</button>
            )}
          </div>
          <button className="verse-search-close" onClick={handleClose}>✕</button>
        </div>

        <div className="verse-search-results">
          {!query && (
            <div className="verse-search-hint">
              <p>{t('verseSearch.hint')}</p>
              <div className="verse-search-examples">
                <button onClick={() => search('Ioan 3:16')}>Ioan 3:16</button>
                <button onClick={() => search('dragoste')}>dragoste</button>
                <button onClick={() => search('Psalmii 23')}>Psalmii 23</button>
              </div>
            </div>
          )}

          {loading && <div className="verse-search-status">{t('verseSearch.loading')}</div>}
          {error && <div className="verse-search-status verse-search-error">{t('verseSearch.error')}</div>}
          {!loading && !error && query.length >= 2 && results.length === 0 && (
            <div className="verse-search-status">{t('verseSearch.noResults')}</div>
          )}
          {!loading && query.length > 0 && query.length < 2 && (
            <div className="verse-search-status">{t('verseSearch.minChars')}</div>
          )}

          {results.map((v, i) => (
            <button
              key={`${v.book}-${v.chapter}-${v.verse}-${i}`}
              className={`verse-search-item ${expandedIdx === i ? 'expanded' : ''}`}
              onClick={() => setExpandedIdx(expandedIdx === i ? null : i)}
            >
              <span className="verse-search-ref">{v.ref}</span>
              <p className="verse-search-text">{v.text}</p>
            </button>
          ))}

          {results.length > 0 && (
            <div className="verse-search-count">{t('verseSearch.count', { n: results.length })}</div>
          )}
        </div>
      </div>
    </div>
  );
}
